import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import logo from "../assets/logo.jpg";

const getStrength = (pwd) => {
  if (!pwd) return { score: 0, label: "", color: "" };
  let score = 0;
  if (pwd.length >= 8)             score++;
  if (pwd.length >= 12)            score++;
  if (/[A-Z]/.test(pwd))           score++;
  if (/[0-9]/.test(pwd))           score++;
  if (/[^A-Za-z0-9]/.test(pwd))   score++;
  const levels = [
    { label: "Too short",   color: "#ef4444" },
    { label: "Weak",        color: "#f97316" },
    { label: "Fair",        color: "#eab308" },
    { label: "Good",        color: "#84cc16" },
    { label: "Strong",      color: "#22c55e" },
    { label: "Very strong", color: "#10b981" },
  ];
  return { score, ...levels[score] };
};

const EyeIcon = ({ open }) =>
  open ? (
    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ) : (
    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );

const Rule = ({ met, text }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem", color: met ? "#166534" : "#64748b" }}>
    {met
      ? <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#22c55e" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
      : <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
    }
    {text}
  </div>
);

const ForgotPassword = () => {
  const [username,        setUsername]        = useState("");
  const [newPassword,     setNewPassword]     = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew,         setShowNew]         = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [error,           setError]           = useState("");
  const [success,         setSuccess]         = useState("");
  const [loading,         setLoading]         = useState(false);

  const strength = getStrength(newPassword);

  const rules = [
    { met: newPassword.length >= 8,           text: "At least 8 characters" },
    { met: /[A-Z]/.test(newPassword),         text: "One uppercase letter" },
    { met: /[a-z]/.test(newPassword),         text: "One lowercase letter" },
    { met: /[0-9]/.test(newPassword),         text: "One number" },
    { met: /[^A-Za-z0-9]/.test(newPassword),  text: "One special character" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const failedRule = rules.find((r) => !r.met);
    if (failedRule) {
      setError(`Password does not meet requirements: ${failedRule.text}`);
      return;
    }

    setLoading(true);
    try {
      const API_BASE_URL = window.__ENV__?.API_BASE_URL ?? "";
      const res = await axios.post(
        `${API_BASE_URL}/auth/forgot-password`,
        {
          username:         username.trim().toLowerCase(),
          new_password:     newPassword,
          confirm_password: confirmPassword,
        },
        { withCredentials: true }
      );
      // Locked and non-existent accounts get the same generic message from the
      // backend (anti-enumeration — see API/auth/auth_routes.py forgot_password).
      // Only the exact real-success message confirms the password actually changed;
      // anything else must NOT be shown as a success, or a locked user would
      // believe their password was reset when nothing happened.
      const message = res.data?.message || "";
      if (message === "Password updated successfully. You can now log in.") {
        setSuccess(message);
      } else {
        setError(message || "Unable to reset password. Please try again.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to reset password. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // wrapper: full-width + bottom margin matching the CSS input rule
  const inputWrap = { position: "relative", width: "100%", marginBottom: 15, display: "block" };
  // eye button: override .login-form-container button width:100% with auto
  const eyeBtn = {
    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
    width: "auto", padding: 0,
    background: "none", border: "none", cursor: "pointer", color: "#64748b",
  };

  return (
    <div id="login-page">
      <div className="login-form-container">
        <div className="logo-container">
          <img src={logo} alt="Weave Logo" className="logo-img" draggable="false" />
        </div>
        <h2>Reset Password</h2>

        {success ? (
          <div style={{ textAlign: "center" }}>
            <p style={{
              color: "#166534", background: "#dcfce7", borderRadius: 6,
              padding: "10px 14px", fontSize: "0.9rem", marginBottom: 16,
            }}>
              {success}
            </p>
            <Link to="/login" className="btn btn-primary w-100" style={{ display: "block" }}>
              Back to Login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <input
              type="text"
              placeholder="Email / Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
            />

            <div style={inputWrap}>
              <input
                type={showNew ? "text" : "password"}
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                style={{ width: "100%", marginBottom: 0, paddingRight: 40 }}
              />
              <button type="button" style={eyeBtn} onClick={() => setShowNew((v) => !v)} tabIndex={-1} title={showNew ? "Hide password" : "Show password"} aria-label={showNew ? "Hide password" : "Show password"}>
                <EyeIcon open={showNew} />
              </button>
            </div>

            {newPassword && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      style={{
                        flex: 1, height: 4, borderRadius: 2,
                        background: i <= strength.score ? strength.color : "#e2e8f0",
                        transition: "background 0.2s",
                      }}
                    />
                  ))}
                </div>
                {strength.label && (
                  <span style={{ fontSize: "0.75rem", color: strength.color, fontWeight: 600 }}>
                    {strength.label}
                  </span>
                )}
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", marginTop: 6 }}>
                  {rules.map((r) => <Rule key={r.text} met={r.met} text={r.text} />)}
                </div>
              </div>
            )}

            <div style={inputWrap}>
              <input
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={{ width: "100%", marginBottom: 0, paddingRight: 40 }}
              />
              <button type="button" style={eyeBtn} onClick={() => setShowConfirm((v) => !v)} tabIndex={-1} title={showConfirm ? "Hide password" : "Show password"} aria-label={showConfirm ? "Hide password" : "Show password"}>
                <EyeIcon open={showConfirm} />
              </button>
            </div>

            {confirmPassword && newPassword !== confirmPassword && (
              <p style={{ color: "#dc2626", fontSize: "0.78rem", marginTop: -8, marginBottom: 8 }}>
                Passwords do not match.
              </p>
            )}

            <button type="submit" disabled={loading}>
              {loading ? "Resetting..." : "Reset Password"}
            </button>

            {error && <p className="login-error">{error}</p>}

            <p style={{ textAlign: "center", marginTop: 12, marginBottom: 0, fontSize: "0.85rem" }}>
              <Link to="/login" style={{ color: "#3b82f6" }}>
                Back to Login
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
